import { faRobot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form';

import { CheckPermissions } from '@/components/CheckPermissions';
import PhoneConsentFields from '@/components/PhoneConsentFields';
import RolePermissionDropdown from '@/components/RolePermissions/RolePermissions.dropdown';
import useFetchRolePermissions from '@/components/RolePermissions/RolePermissions.hooks';
import type { Role } from '@/components/RolePermissions/RolePermissions.types';
import { PERMISSIONS } from '@/constants/auth.constants';
import { PERMISSIONS_LOGIC } from '@/constants/common';
import {
  mapUserNotificationSettingsToFormFields,
  NotificationSettingsForm
} from '@/features/NotificationSettings';
import type {
  UserEditDialogFormFields,
  UserRole
} from '@/pages/Administration/Users';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { getResponseErrorMessages } from '@/services/BaseApi.service';
import { checkPermissions } from '@/services/permissions';
import { userAtom } from '@/store/user.store';
import type { UserInfo } from '@/types/common';

import { useUserEditDialog } from './UserEditDialog.hooks';
import useStyles from './UserEditDialog.style';
import type {
  UserEditDialogFormValues,
  UserEditDialogProps
} from './UserEditDialog.types';

const UserEditDialog = ({
  open,
  onClose,
  user: userData
}: UserEditDialogProps) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [currentUser, setCurrentUser] = useAtom(userAtom);
  const { classes } = useStyles();
  const theme = useTheme();
  const { mutation, refetch, roleTypes, user } = useUserEditDialog(userData);
  const canEditConsent = user.id !== -1 && currentUser?.id === user.id;

  //-----------------------------------------------------------------
  const form = useForm<UserEditDialogFormValues>();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = form;

  // -----------------------------------------------------------------
  const { data: allRolesData } = useFetchRolePermissions();
  const allRoles =
    allRolesData?.map((role: Role) => ({
      name: role.name,
      type: role.type,
      permissions: role.permissions
    })) || [];
  const roles = useWatch({ control, name: 'roles' });
  const selectedRoles = roles || [];
  const isServiceAccount = useWatch({ control, name: 'is_service_account' });

  //-----------------------------------------------------------------
  const CURRENT_USER_CAN_UPDATE_NOTIFICATIONS = checkPermissions(
    [PERMISSIONS.USERS_NOTIFICATIONS],
    currentUser,
    selectedOrganization
  );

  const IS_ALL_ORG = selectedOrganization.id === 0;

  //-----------------------------------------------------------------
  const handleDialogClose = () => {
    onClose();
    reset();
    mutation.reset();
  };

  //-----------------------------------------------------------------
  const errorMessage = useMemo(() => {
    if (errors.notification_ticket_event) {
      return errors.notification_ticket_event.message
        ? `${errors.notification_ticket_event.message}`
        : 'Please select at least one ticket event.';
    }
    return null;
  }, [errors.notification_ticket_event]);

  const roleOptions: UserRole[] = useMemo(() => {
    const type = selectedOrganization.id === 1 ? 'global' : 'organization';
    return (
      roleTypes
        .filter((r) => r.type === type)
        // Filter out LOGS role for service accounts
        .filter((r) => !isServiceAccount || r.name !== 'logs')
        .map((r) => ({ role_id: r.id, name: r.name }))
    );
  }, [roleTypes, selectedOrganization.id, isServiceAccount]);

  useEffect(() => {
    if (open) {
      // create new user
      if (userData.id === -1) {
        reset({
          id: -1,
          name: '',
          email: '',
          phone_number: '',
          sms_consent: false,
          tos_consent: false,
          roles: [],
          is_service_account: false,
          notification_announcement: true,
          notification_ticket_service: true,
          notification_ticket_threat: true,
          notification_ticket_threat_priority_p1: true,
          notification_ticket_threat_priority_p2: true,
          notification_ticket_threat_priority_p3: true,
          notification_ticket_threat_priority_p4: true,
          notification_ticket_threat_priority_p5: true,
          notification_tags: [],
          notification_ticket_event_create: true,
          notification_ticket_event_update: true,
          add_to_all_tickets: false
        });
        return;
      }

      // edit existing user
      reset({
        ...userData,
        ...mapUserNotificationSettingsToFormFields(userData),
        notification_ticket_event: userData.notification_ticket_event ?? null
      });
      refetch();
    }
  }, [open]);

  // Remove LOGS role when service account is checked
  useEffect(() => {
    const currentRoles = form.getValues('roles') || [];

    if (isServiceAccount && currentRoles.length > 0) {
      // Find and remove LOGS role if present
      const logsRole = currentRoles.find(
        (r: UserRole) => r.name.toLowerCase() === 'logs'
      );
      if (logsRole) {
        const filteredRoles = currentRoles.filter(
          (r: UserRole) => r.name.toLowerCase() !== 'logs'
        );
        form.setValue('roles', filteredRoles);
      }
    }
  }, [isServiceAccount, form]);

  // TODO: formdata has no typing, need to fix this
  const onSubmit: SubmitHandler<UserEditDialogFormValues> = (formData) => {
    // filter out falsy values
    const notification_threat_priority = [
      formData.notification_ticket_threat_priority_p1 && 1,
      formData.notification_ticket_threat_priority_p2 && 2,
      formData.notification_ticket_threat_priority_p3 && 3,
      formData.notification_ticket_threat_priority_p4 && 4,
      formData.notification_ticket_threat_priority_p5 && 5
    ].filter((value): value is number => Boolean(value));

    // Convert checkbox values to array format
    const notification_ticket_event = [
      formData.notification_ticket_event_create && 'CREATE',
      formData.notification_ticket_event_update && 'UPDATE'
    ].filter((value): value is 'CREATE' | 'UPDATE' => Boolean(value));

    const requestBody: UserEditDialogFormFields = {
      ...formData,
      is_service_account: formData.is_service_account || false,
      notification_threat_priority,
      notification_announcement: formData.notification_announcement,
      notification_ticket_service: formData.notification_ticket_service,
      notification_ticket_threat: formData.notification_ticket_threat,
      notification_tags: formData.notification_tags,
      notification_ticket_event
    };

    mutation.mutate(requestBody, {
      onSuccess: (res) => {
        enqueueSnackbar(`User ${user.id === -1 ? 'Created' : 'Updated'}`, {
          variant: 'success'
        });
        onClose('success');

        // update userAtom with latest preferences if user edited is the current user
        if (formData.id === currentUser?.id) {
          setCurrentUser({
            ...currentUser,
            notification_announcement: res.data.notification_announcement,
            notification_ticket_service: res.data.notification_ticket_service,
            notification_ticket_threat: res.data.notification_ticket_threat,
            notification_threat_priority: res.data.notification_threat_priority,
            notification_tags: res.data.notification_tags,
            notification_ticket_event: res.data.notification_ticket_event
          } as UserInfo);
        }
      }
    });
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="md">
      <DialogTitle>
        {user.id !== -1 ? `Edit User (#${user.id})` : 'Create User'}
      </DialogTitle>
      <DialogContent>
        {!IS_ALL_ORG && (
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2} sx={{ pt: 2 }}>
                <Box>
                  <InputLabel>Organization</InputLabel>
                  <TextField
                    fullWidth
                    value={selectedOrganization.name}
                    disabled
                    slotProps={{ htmlInput: { maxLength: 64 } }}
                  />
                </Box>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flexGrow: 1 }}>
                    <InputLabel id="userName">Full Name</InputLabel>
                    <Controller
                      name="name"
                      rules={{ required: true, validate: (v) => !!v?.trim() }}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          slotProps={{ htmlInput: { maxLength: 64 } }}
                          error={!!errors.name}
                          autoComplete="off"
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <InputLabel id="userName">Email Address</InputLabel>
                    <Controller
                      name="email"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          error={!!errors.email}
                          type="email"
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <PhoneConsentFields
                  control={control}
                  errors={errors}
                  consentEditable={canEditConsent}
                  showConsentControls={user.id !== -1}
                />
                {user.id === -1 && (
                  <Box>
                    <Controller
                      name="is_service_account"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value || false}
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <FontAwesomeIcon icon={faRobot} />
                              <Typography>
                                Service Account (API Access Only)
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    />
                    {isServiceAccount && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Service accounts cannot log in to the Scope UI. They can
                        only authenticate using API keys.
                      </Alert>
                    )}
                  </Box>
                )}
                <Divider />
                <Box>
                  <InputLabel>Roles</InputLabel>
                  <FormControl fullWidth>
                    <Controller
                      name="roles"
                      control={control}
                      rules={{ required: true }}
                      defaultValue={user.roles || []}
                      render={({ field: { onChange } }) => (
                        <Autocomplete
                          className={classes.autocomplete}
                          multiple
                          options={roleOptions}
                          getOptionLabel={(r) => r.name.toUpperCase()}
                          getOptionKey={(r) => r.role_id}
                          defaultValue={user.roles || []}
                          onChange={(_e, values) => onChange(values)}
                          isOptionEqualToValue={(option, v) =>
                            option.role_id === v.role_id
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              error={!!errors.roles}
                            />
                          )}
                        />
                      )}
                    />
                  </FormControl>
                </Box>
                <CheckPermissions
                  permissions={[PERMISSIONS.INTERNAL, PERMISSIONS.USERS_DELETE]}
                  logic={PERMISSIONS_LOGIC.OR}
                >
                  <Box>
                    <RolePermissionDropdown
                      userRoles={selectedRoles}
                      allRoles={allRoles}
                    />
                  </Box>
                </CheckPermissions>
                {user.id === -1 && !isServiceAccount && (
                  <Box>
                    <Controller
                      name="add_to_all_tickets"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value || false}
                              onChange={onChange}
                              color="primary"
                            />
                          }
                          label="Add user as a watcher to all existing tickets"
                        />
                      )}
                    />
                  </Box>
                )}
                {user.id !== -1 && !user.is_service_account && (
                  <>
                    <Divider />
                    {errorMessage && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        {errorMessage}
                      </Alert>
                    )}
                    <Box>
                      <InputLabel
                        style={{
                          color: !CURRENT_USER_CAN_UPDATE_NOTIFICATIONS
                            ? theme.vars.palette.text.disabled
                            : undefined
                        }}
                        sx={{ fontSize: '1.25rem' }}
                      >
                        Email Notification Preferences
                      </InputLabel>
                      <NotificationSettingsForm
                        permissionsCheck={
                          !CURRENT_USER_CAN_UPDATE_NOTIFICATIONS
                        }
                      />
                    </Box>
                  </>
                )}
                {user.id !== -1 && <Box />}
                {!!mutation.error && (
                  <Alert severity="error">
                    {getResponseErrorMessages(mutation.error as AxiosError)}
                  </Alert>
                )}
              </Stack>
            </form>
          </FormProvider>
        )}
        {IS_ALL_ORG && user.organizations.length === 0 && (
          <Alert severity="info" sx={{ my: 4 }}>
            <Typography variant="body1">
              Please select an organization to create or edit users.
            </Typography>
          </Alert>
        )}
        {IS_ALL_ORG && user.organizations.length > 0 && (
          <Alert severity="info" sx={{ my: 4 }}>
            <Typography variant="body1">
              Please select the respective organization to edit the user. The
              user {user.name} is currently associated with the following
              Organization(s):
              <Box>
                <strong>
                  {user.organizations.map((o) => o.name).join(', ')}
                </strong>{' '}
              </Box>
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>Cancel</Button>
        {!IS_ALL_ORG && (
          <Button
            variant="contained"
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={mutation.isPending}
          >
            {user.id !== -1 ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserEditDialog;
